// ============================================================================
// THE CYCLE — AIO Explorer
// All-in-one analytics hub: Stat Leaders, Pitch Lab, At-Bats, Matchups,
// Compare Players, Compare Teams, and Salary Intelligence.
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Chip, Avatar,
  CircularProgress, Alert, ToggleButton, ToggleButtonGroup, Tooltip,
  TextField, InputAdornment, Slider, useTheme, alpha, Stack, Button,
  Divider, Select, MenuItem, FormControl, InputLabel, IconButton,
  Tab, Tabs, Autocomplete, LinearProgress, Grid, Paper, Skeleton,
  Badge
} from '@mui/material';
import {
  Search, SportsBaseball, FilterList, TrendingUp, Speed,
  Whatshot, BubbleChart, CompareArrows, ScatterPlot,
  EmojiEvents, PersonSearch, Groups, ArrowUpward, ArrowDownward,
  AttachMoney, Close, Add, Assessment, Download
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  pitchSplitsApi, analyticsApi, playersApi,
  statsApi, teamsApi
} from '../services/apiService';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Cell, ScatterChart, Scatter, ReferenceLine,
} from 'recharts';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';
import useMobile from '../hooks/useMobile';
import AtBatExplorer from '../components/AtBatExplorer';
import SprayChartExplorer from '../components/SprayChartExplorer';
import { themeUtils } from '../theme/theme';
import { playerProfileUrl, nameToSlug, makePlayerSlug } from '../utils/playerSlug';
// import { useSettings } from '../context/SettingsContext'; // unused after Leaders removal

// ============================================================================
// PITCH TYPE DISPLAY CONFIG
// ============================================================================

import { PITCH_NAMES as PITCH_TYPE_NAMES, PITCH_COLORS as PITCH_TYPE_COLORS, getPitchName } from '../utils/pitchTypes';

// Stuff+ grade thresholds
function gradeStuffPlus(value) {
  if (value >= 130) return { grade: 'A+', color: '#00C851' };
  if (value >= 120) return { grade: 'A', color: '#2e7d32' };
  if (value >= 115) return { grade: 'A-', color: '#4CAF50' };
  if (value >= 110) return { grade: 'B+', color: '#8BC34A' };
  if (value >= 105) return { grade: 'B', color: '#CDDC39' };
  if (value >= 100) return { grade: 'B-', color: '#FFC107' };
  if (value >= 95) return { grade: 'C+', color: '#FF9800' };
  if (value >= 90) return { grade: 'C', color: '#FF5722' };
  if (value >= 85) return { grade: 'C-', color: '#f44336' };
  if (value >= 75) return { grade: 'D', color: '#c62828' };
  return { grade: 'F', color: '#880e4f' };
}

// ============================================================================
// STAT LEADER CONFIG
// ============================================================================

const BATTING_STATS = [
  { key: 'war', label: 'WAR', desc: 'Wins Above Replacement', format: v => v?.toFixed(1) },
  { key: 'cvr', label: 'CVR', desc: 'Composite Value Rating (0-100)', format: v => v?.toFixed(1) },
  { key: 'avg', label: 'AVG', desc: 'Batting Average', format: v => v?.toFixed(3)?.replace(/^0/, '') },
  { key: 'ops', label: 'OPS', desc: 'On-Base + Slugging', format: v => v?.toFixed(3)?.replace(/^0/, '') },
  { key: 'opsPlus', label: 'OPS+', desc: 'OPS+ (100 = league avg)', format: v => Math.round(v) },
  { key: 'wrcPlus', label: 'wRC+', desc: 'Weighted Runs Created+', format: v => Math.round(v) },
  { key: 'woba', label: 'wOBA', desc: 'Weighted On-Base Average', format: v => v?.toFixed(3)?.replace(/^0/, '') },
  { key: 'iso', label: 'ISO', desc: 'Isolated Power', format: v => v?.toFixed(3)?.replace(/^0/, '') },
  { key: 'babip', label: 'BABIP', desc: 'Batting AVG on Balls in Play', format: v => v?.toFixed(3)?.replace(/^0/, '') },
  { key: 'hr', label: 'HR', desc: 'Home Runs', format: v => v },
  { key: 'rbi', label: 'RBI', desc: 'Runs Batted In', format: v => v },
  { key: 'sb', label: 'SB', desc: 'Stolen Bases', format: v => v },
];

const PITCHING_STATS = [
  { key: 'war', label: 'WAR', desc: 'Wins Above Replacement', format: v => v?.toFixed(1) },
  { key: 'cvr', label: 'CVR', desc: 'Composite Value Rating (0-100)', format: v => v?.toFixed(1) },
  { key: 'era', label: 'ERA', desc: 'Earned Run Average', format: v => v?.toFixed(2), ascending: true },
  { key: 'fip', label: 'FIP', desc: 'Fielding Independent Pitching', format: v => v?.toFixed(2), ascending: true },
  { key: 'eraPlus', label: 'ERA+', desc: 'ERA+ (100 = league avg)', format: v => Math.round(v) },
  { key: 'k9', label: 'K/9', desc: 'Strikeouts per 9 innings', format: v => v?.toFixed(1) },
  { key: 'bb9', label: 'BB/9', desc: 'Walks per 9 innings', format: v => v?.toFixed(1), ascending: true },
  { key: 'whip', label: 'WHIP', desc: 'Walks + Hits per IP', format: v => v?.toFixed(2), ascending: true },
  { key: 'ip', label: 'IP', desc: 'Innings Pitched', format: v => v?.toFixed(1) },
];

// Stat value color based on league context
function getStatColor(key, value, theme) {
  if (value == null) return 'text.secondary';
  const good = theme.palette.success.main;
  const avg = theme.palette.warning.main;
  const bad = theme.palette.error.main;
  const thresholds = {
    war: [3, 1.5], cvr: [70, 50], avg: [0.28, 0.24], ops: [0.8, 0.7],
    opsPlus: [120, 100], wrcPlus: [120, 100], woba: [0.34, 0.31],
    iso: [0.2, 0.14], hr: [25, 15], era: [3.2, 4.2], fip: [3.3, 4.0],
    eraPlus: [120, 100], k9: [9, 7], whip: [1.1, 1.3],
  };
  const t = thresholds[key];
  if (!t) return 'text.primary';
  const ascending = ['era', 'fip', 'bb9', 'whip'].includes(key);
  if (ascending) return value <= t[0] ? good : value <= t[1] ? avg : bad;
  return value >= t[0] ? good : value >= t[1] ? avg : bad;
}

// ============================================================================
// SHARED CONSTANTS
// ============================================================================

const ALL_TEAMS = [
  { abbr: 'ARI', name: 'Arizona Diamondbacks' }, { abbr: 'ATL', name: 'Atlanta Braves' },
  { abbr: 'BAL', name: 'Baltimore Orioles' }, { abbr: 'BOS', name: 'Boston Red Sox' },
  { abbr: 'CHC', name: 'Chicago Cubs' }, { abbr: 'CWS', name: 'Chicago White Sox' },
  { abbr: 'CIN', name: 'Cincinnati Reds' }, { abbr: 'CLE', name: 'Cleveland Guardians' },
  { abbr: 'COL', name: 'Colorado Rockies' }, { abbr: 'DET', name: 'Detroit Tigers' },
  { abbr: 'HOU', name: 'Houston Astros' }, { abbr: 'KC', name: 'Kansas City Royals' },
  { abbr: 'LAA', name: 'Los Angeles Angels' }, { abbr: 'LAD', name: 'Los Angeles Dodgers' },
  { abbr: 'MIA', name: 'Miami Marlins' }, { abbr: 'MIL', name: 'Milwaukee Brewers' },
  { abbr: 'MIN', name: 'Minnesota Twins' }, { abbr: 'NYM', name: 'New York Mets' },
  { abbr: 'NYY', name: 'New York Yankees' }, { abbr: 'OAK', name: 'Oakland Athletics' },
  { abbr: 'PHI', name: 'Philadelphia Phillies' }, { abbr: 'PIT', name: 'Pittsburgh Pirates' },
  { abbr: 'SD', name: 'San Diego Padres' }, { abbr: 'SF', name: 'San Francisco Giants' },
  { abbr: 'SEA', name: 'Seattle Mariners' }, { abbr: 'STL', name: 'St. Louis Cardinals' },
  { abbr: 'TB', name: 'Tampa Bay Rays' }, { abbr: 'TEX', name: 'Texas Rangers' },
  { abbr: 'TOR', name: 'Toronto Blue Jays' }, { abbr: 'WSH', name: 'Washington Nationals' },
];

const LUXURY_TAX_THRESHOLD = 241000000;
const formatSalary = (val) => {
  if (val == null || isNaN(val)) return '—';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
};
const formatFullSalary = (val) => {
  if (val == null || isNaN(val)) return '—';
  return `$${val.toLocaleString()}`;
};

// ============================================================================
// MAIN EXPLORER COMPONENT
// ============================================================================

const Explorer = () => {
  const theme = useTheme();
  const { year } = useYear();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Deep-link support
  const TAB_KEYS = {
    'pitch-lab': 0, 'at-bats': 1,
    'compare': 2, 'compare-teams': 3, 'spray-charts': 4,
    // legacy redirects
    'stat-leaders': 0, 'matchups': 2,
  };
  const initialTab = TAB_KEYS[searchParams.get('tab')] ?? 0;
  const [tab, setTab] = useState(initialTab);

  const deepLinkPlayer = useMemo(() => {
    const team = searchParams.get('team');
    const player = searchParams.get('player');
    if (team && player) return { team, playerName: player };
    return null;
  }, [searchParams]);

  const deepLinkPerspective = searchParams.get('perspective') || 'batter';
  const deepLinkDate = searchParams.get('date') || '';
  const deepLinkVs = searchParams.get('vs') || '';
  const deepLinkInning = searchParams.get('inning') ? parseInt(searchParams.get('inning')) : null;

  const tabs = [
    { icon: <Speed sx={{ fontSize: 16 }} />, label: 'Pitch Lab' },
    { icon: <SportsBaseball sx={{ fontSize: 16 }} />, label: 'At-Bats' },
    { icon: <CompareArrows sx={{ fontSize: 16 }} />, label: 'Compare' },
    { icon: <Groups sx={{ fontSize: 16 }} />, label: 'Teams' },
    { icon: <ScatterPlot sx={{ fontSize: 16 }} />, label: 'Spray Charts' },
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 2, flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main || theme.palette.primary.dark})`,
          }}>
            <BubbleChart sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              Explorer
            </Typography>
          </Box>
        </Box>
        <PageYearSelector />
      </Box>

      {/* Tab Navigation */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          '& .MuiTab-root': {
            fontWeight: 700, textTransform: 'none', fontSize: '0.82rem',
            minHeight: 40, py: 0.75, px: 1.5,
          },
          '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
        }}
      >
        {tabs.map((t, i) => (
          <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />
        ))}
      </Tabs>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {tab === 0 && <PitchExplorer year={year} isMobile={isMobile} navigate={navigate} />}
          {tab === 1 && <AtBatExplorerTab
            year={year}
            isMobile={isMobile}
            deepLinkPlayer={deepLinkPlayer}
            deepLinkPerspective={deepLinkPerspective}
            deepLinkDate={deepLinkDate}
            deepLinkVs={deepLinkVs}
            deepLinkInning={deepLinkInning}
          />}
          {tab === 2 && <ComparePlayersTab year={year} isMobile={isMobile} navigate={navigate} />}
          {tab === 3 && <CompareTeamsTab year={year} isMobile={isMobile} navigate={navigate} />}
          {tab === 4 && <SprayChartExplorer year={year} />}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};


// ============================================================================
// PITCH EXPLORER TAB (Pitch Lab)
// ============================================================================

const PitchExplorer = ({ year, isMobile, navigate }) => {
  const theme = useTheme();
  const [leaders, setLeaders] = useState([]);
  const [baselines, setBaselines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBaselines, setShowBaselines] = useState(false);

  // Filters
  const [pitchType, setPitchType] = useState('');
  const isCurrentYear = year >= new Date().getFullYear();
  const defaultMin = isCurrentYear ? 50 : 200;
  const [minPitches, setMinPitches] = useState(defaultMin);

  // Batch pitch type + minPitches change together to avoid double-fetch race
  const handlePitchTypeChange = (code) => {
    setPitchType(code);
    setMinPitches(code ? (isCurrentYear ? 25 : 75) : (isCurrentYear ? 50 : 200));
  };
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('stuffPlus');
  const [sortDir, setSortDir] = useState('desc');
  const [gameType, setGameType] = useState('R');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [leaderData, baselineData] = await Promise.all([
        pitchSplitsApi.getStuffPlusLeaderboard(year, {
          pitchType: pitchType || undefined,
          limit: 500,
          minPitches,
          gameType: gameType !== 'all' ? gameType : undefined,
        }),
        pitchSplitsApi.getBaselines(year).catch(() => null),
      ]);
      setLeaders(leaderData?.leaders || []);
      setBaselines(baselineData?.pitchTypes || leaderData?.baselines || null);
    } catch (err) {
      setError('Unable to load pitch data');
    } finally {
      setLoading(false);
    }
  }, [year, pitchType, minPitches, gameType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredLeaders = useMemo(() => {
    if (!search) return leaders;
    const q = search.toLowerCase();
    return leaders.filter(p => {
      const pName = (p.player || p.name || '').toLowerCase();
      return pName.includes(q) || (p.team || '').toLowerCase().includes(q);
    });
  }, [leaders, search]);

  const sortedLeaders = useMemo(() => {
    const arr = [...filteredLeaders];
    arr.sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (aVal == null) aVal = -Infinity;
      if (bVal == null) bVal = -Infinity;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return arr;
  }, [filteredLeaders, sortBy, sortDir]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const handlePlayerClick = (leader) => {
    const name = (leader.player || leader.name || '').replace(/_/g, ' ');
    if (name) navigate(playerProfileUrl(name));
  };

  const FILTER_TYPES = ['', 'FF', 'SI', 'FC', 'SL', 'ST', 'CU', 'CH', 'FS', 'KC'];

  return (
    <>
      {/* Filters */}
      <Card sx={{ mb: 2, p: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search pitcher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
            sx={{ minWidth: 180, maxWidth: 250 }}
          />

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {FILTER_TYPES.map(code => (
              <Chip
                key={code || 'all'}
                label={code ? (PITCH_TYPE_NAMES[code] || code) : 'Overall'}
                size="small"
                variant={pitchType === code ? 'filled' : 'outlined'}
                onClick={() => handlePitchTypeChange(code)}
                sx={{
                  fontWeight: pitchType === code ? 700 : 500,
                  bgcolor: pitchType === code ? alpha(PITCH_TYPE_COLORS[code] || theme.palette.primary.main, 0.2) : undefined,
                  borderColor: PITCH_TYPE_COLORS[code] || undefined,
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>

          <ToggleButtonGroup
            size="small" value={gameType} exclusive
            onChange={(_, v) => v && setGameType(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.3, fontSize: '0.75rem', textTransform: 'none' } }}
          >
            <ToggleButton value="R">Regular</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={`Minimum ${minPitches} pitches`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 160 }}>
              <FilterList fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Min {minPitches}P</Typography>
              <Slider
                size="small" min={10} max={1500} step={10}
                value={minPitches} onChange={(_, v) => setMinPitches(v)}
                sx={{ ml: 0.5, width: 100 }}
              />
            </Box>
          </Tooltip>

          {baselines && (
            <Chip
              icon={<Whatshot sx={{ fontSize: 16 }} />}
              label="Baselines"
              size="small"
              variant={showBaselines ? 'filled' : 'outlined'}
              onClick={() => setShowBaselines(!showBaselines)}
              color={showBaselines ? 'primary' : 'default'}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
          )}
        </Stack>
      </Card>

      {/* Collapsible League Baselines */}
      <AnimatePresence>
        {showBaselines && baselines && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
            <Card sx={{ mb: 2, p: 0, background: alpha(theme.palette.primary.main, 0.02), border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
                <Whatshot sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700}>{year} League Pitch Baselines</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, pl: 2 }}>Pitch</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>Velo</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>IVB</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>HB</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>Whiff%</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>Spin</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, pr: 2 }}>Pitchers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(baselines)
                      .filter(([code]) => PITCH_TYPE_NAMES[code])
                      .sort((a, b) => (b[1].pitcherCount || 0) - (a[1].pitcherCount || 0))
                      .slice(0, 12)
                      .map(([code, data]) => (
                        <TableRow
                          key={code} hover
                          onClick={() => { setPitchType(code); setShowBaselines(false); }}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(PITCH_TYPE_COLORS[code] || theme.palette.primary.main, 0.06) } }}
                        >
                          <TableCell sx={{ py: 0.4, pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PITCH_TYPE_COLORS[code] || '#666', flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>{PITCH_TYPE_NAMES[code]}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{data.velo}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', ml: 0.3 }}>±{data.stdVelo}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{data.ivb}"</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{data.hb}"</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem', color: (data.whiff || 0) >= 30 ? theme.palette.success.main : 'inherit' }}>
                              {data.whiff}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{data.spin}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, pr: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{data.pitcherCount}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={36} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Leaderboard Table */}
      {!loading && !error && (
        <Card sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `2px solid ${theme.palette.divider}` } }}>
                  <TableCell sx={{ fontWeight: 700, width: 36, pl: 1.5, fontSize: '0.75rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 180, fontSize: '0.75rem' }}>Pitcher</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={sortBy === 'stuffPlus'} direction={sortBy === 'stuffPlus' ? sortDir : 'desc'} onClick={() => handleSort('stuffPlus')}>
                      Stuff+
                    </TableSortLabel>
                  </TableCell>
                  {pitchType ? (
                    <>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        <TableSortLabel active={sortBy === 'avgVelo'} direction={sortBy === 'avgVelo' ? sortDir : 'desc'} onClick={() => handleSort('avgVelo')}>
                          Velo
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>IVB</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>HB</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        <TableSortLabel active={sortBy === 'whiffRate'} direction={sortBy === 'whiffRate' ? sortDir : 'desc'} onClick={() => handleSort('whiffRate')}>
                          Whiff%
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Spin</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Usage</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Count</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Grade</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Hand</TableCell>
                      {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Pitches</TableCell>}
                      {!isMobile && <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Arsenal</TableCell>}
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLeaders.map((leader, idx) => {
                  const { grade, color } = gradeStuffPlus(leader.stuffPlus);
                  return (
                    <TableRow
                      key={`${leader.team}-${leader.player}-${idx}`}
                      hover
                      onClick={() => handlePlayerClick(leader)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    >
                      <TableCell sx={{ pl: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{idx + 1}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={getTeamLogoUrl(leader.team)} sx={{ width: 24, height: 24 }} alt={leader.team} />
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>{leader.player}</Typography>
                            <Typography variant="caption" color="text.secondary">{leader.team}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight={800} sx={{ color, fontSize: '1rem' }}>{leader.stuffPlus}</Typography>
                      </TableCell>
                      {pitchType ? (
                        <>
                          <TableCell align="center">{leader.avgVelo || '—'}</TableCell>
                          <TableCell align="center">{leader.avgVertBreak ? `${leader.avgVertBreak}"` : '—'}</TableCell>
                          <TableCell align="center">{leader.avgHorizBreak ? `${leader.avgHorizBreak}"` : '—'}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{leader.whiffRate != null ? `${leader.whiffRate}%` : '—'}</TableCell>
                          <TableCell align="center">{leader.avgSpin || '—'}</TableCell>
                          <TableCell align="center">{leader.usage != null ? `${leader.usage}%` : '—'}</TableCell>
                          <TableCell align="center">{leader.count || '—'}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="center">
                            <Chip label={grade} size="small" sx={{ fontWeight: 800, bgcolor: alpha(color, 0.15), color, minWidth: 36 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{leader.hand || '—'}</Typography>
                          </TableCell>
                          {!isMobile && <TableCell align="center">{leader.totalPitches?.toLocaleString() || '—'}</TableCell>}
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {(leader.topPitches || []).map(p => {
                                  const { color: pColor } = gradeStuffPlus(p.stuffPlus || 100);
                                  return (
                                    <Tooltip key={p.code} title={`${p.name}: ${p.stuffPlus ?? '—'} Stuff+ | ${p.avgVelo ?? '—'} mph | ${p.whiffRate ?? '—'}% whiff | ${p.usage ?? '—'}% usage`}>
                                      <Chip
                                        size="small"
                                        label={`${p.name || getPitchName(p.code)} ${p.stuffPlus ?? '—'}`}
                                        sx={{
                                          fontSize: '0.75rem', fontWeight: 700,
                                          bgcolor: alpha(PITCH_TYPE_COLORS[p.code] || '#666', 0.12),
                                          color: pColor,
                                          border: `1px solid ${alpha(PITCH_TYPE_COLORS[p.code] || '#666', 0.25)}`,
                                          height: 22,
                                        }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" color="text.secondary">
              {sortedLeaders.length} pitcher{sortedLeaders.length !== 1 ? 's' : ''} shown
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Stuff+ calibrated with {year} league baselines
            </Typography>
          </Box>
        </Card>
      )}
    </>
  );
};

// ============================================================================
// AT-BAT EXPLORER TAB
// ============================================================================

// ============================================================================
// AT-BAT EXPLORER TAB
// ============================================================================

const AtBatExplorerTab = ({ year, isMobile, deepLinkPlayer, deepLinkPerspective, deepLinkDate, deepLinkVs, deepLinkInning }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(deepLinkPlayer || null);
  const [perspective, setPerspective] = useState(deepLinkPerspective || 'batter');

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await pitchSplitsApi.searchPitchPlayers(searchQuery, {
          perspective, season: year, limit: 15,
        });
        setSearchResults(results?.players || results || []);
      } catch (err) {
        console.error('Player search error:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, perspective, year]);

  return (
    <Box>
      <Box sx={{
        display: 'flex', gap: 1.5, mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
      }}>
        <Autocomplete
          freeSolo
          options={searchResults}
          getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${(opt.name || opt.player || '').replace(/_/g, ' ')} (${opt.team})`}
          loading={searchLoading}
          inputValue={searchQuery}
          onInputChange={(_, val) => setSearchQuery(val)}
          onChange={(_, val) => {
            if (val && typeof val === 'object') {
              setSelectedPlayer({
                team: val.team,
                playerName: val.name || val.player,
                playerType: val.playerType,
              });
            }
          }}
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.team}-${opt.name || opt.player}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={getTeamLogoUrl(opt.team)} sx={{ width: 24, height: 24 }} />
                <Typography variant="body2" fontWeight={600}>
                  {(opt.name || opt.player || '').replace(/_/g, ' ')}
                </Typography>
                <Chip label={opt.team} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                {opt.playerType && (
                  <Chip label={opt.playerType} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Search ${perspective}s by name...`}
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          sx={{ flex: 1 }}
        />
        <ToggleButtonGroup
          value={perspective}
          exclusive
          onChange={(_, val) => { if (val) { setPerspective(val); setSelectedPlayer(null); setSearchQuery(''); } }}
          size="small"
          sx={{ height: 40, flexShrink: 0 }}
        >
          <ToggleButton value="batter" sx={{ textTransform: 'none', fontSize: '0.8rem', px: 2, fontWeight: 600 }}>
            Batter
          </ToggleButton>
          <ToggleButton value="pitcher" sx={{ textTransform: 'none', fontSize: '0.8rem', px: 2, fontWeight: 600 }}>
            Pitcher
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {selectedPlayer && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
          px: 1.5, py: 1, borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        }}>
          <Avatar src={getTeamLogoUrl(selectedPlayer.team)} sx={{ width: 28, height: 28 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {(selectedPlayer.playerName || '').replace(/_/g, ' ')}
          </Typography>
          <Chip label={selectedPlayer.team} size="small" sx={{ fontWeight: 600, height: 22 }} />
          <Chip label={perspective} size="small" variant="outlined"
            sx={{ fontWeight: 600, height: 22, textTransform: 'capitalize' }} />
          <Box sx={{ flex: 1 }} />
          <Chip
            label="Clear"
            size="small"
            onClick={() => { setSelectedPlayer(null); setSearchQuery(''); }}
            sx={{ fontWeight: 600, height: 22, cursor: 'pointer',
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
          />
        </Box>
      )}

      {selectedPlayer ? (
        <AtBatExplorer
          team={selectedPlayer.team}
          playerName={selectedPlayer.playerName}
          season={year}
          perspective={perspective}
          playerType={selectedPlayer.playerType}
          initialGameDate={deepLinkDate}
          initialVsPlayer={deepLinkVs}
          initialInning={deepLinkInning}
        />
      ) : (
        <Card elevation={0} sx={{
          border: '1px dashed', borderColor: alpha(theme.palette.divider, 0.4), borderRadius: 2,
          p: { xs: 3, sm: 5 }, textAlign: 'center',
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}>
          <SportsBaseball sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Pitch-by-Pitch At-Bat Viewer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: 'auto', lineHeight: 1.6 }}>
            Search any player above to explore their at-bats with zone charts,
            pitch sequences, velocity tracking, and movement profiles.
          </Typography>
        </Card>
      )}
    </Box>
  );
};

// ============================================================================
// COMPARE PLAYERS TAB
// ============================================================================

const GRADE_COLORS = {
  'A+': '#00C851', A: '#4CAF50', 'B+': '#8BC34A', B: '#CDDC39',
  'B-': '#FFEB3B', C: '#FF9800', 'C-': '#FF5722', D: '#F44336', F: '#D32F2F',
};
const CVR_TIER_COLORS = {
  'MVP': '#FFD700', 'Elite': '#00C851', 'All-Star': '#4CAF50',
  'Above Average': '#8BC34A', 'Average': '#FF9800', 'Below Average': '#FF5722',
  'Replacement': '#D32F2F',
};
const CP_BATTING_STATS = [
  { key: 'avg', label: 'AVG', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { key: 'obp', label: 'OBP', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { key: 'slg', label: 'SLG', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { key: 'ops', label: 'OPS', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { key: 'wOBA', label: 'wOBA', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { key: 'homeRuns', label: 'HR', fmt: v => v || 0 },
  { key: 'rbi', label: 'RBI', fmt: v => v || 0 },
  { key: 'stolenBases', label: 'SB', fmt: v => v || 0 },
  { key: 'runs', label: 'R', fmt: v => v || 0 },
  { key: 'hits', label: 'H', fmt: v => v || 0 },
  { key: 'doubles', label: '2B', fmt: v => v || 0 },
  { key: 'strikeOuts', label: 'K', fmt: v => v || 0 },
  { key: 'baseOnBalls', label: 'BB', fmt: v => v || 0 },
  { key: 'babip', label: 'BABIP', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { key: 'iso', label: 'ISO', fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
];
const CP_PITCHING_STATS = [
  { key: 'era', label: 'ERA', fmt: v => v?.toFixed(2) || '—', lower: true },
  { key: 'whip', label: 'WHIP', fmt: v => v?.toFixed(2) || '—', lower: true },
  { key: 'fip', label: 'FIP', fmt: v => v?.toFixed(2) || '—', lower: true },
  { key: 'strikeOuts', label: 'K', fmt: v => v || 0 },
  { key: 'wins', label: 'W', fmt: v => v || 0 },
  { key: 'saves', label: 'SV', fmt: v => v || 0 },
  { key: 'inningsPitched', label: 'IP', fmt: v => v || '—' },
  { key: 'kPer9', label: 'K/9', fmt: v => v?.toFixed(1) || '—' },
  { key: 'bbPer9', label: 'BB/9', fmt: v => v?.toFixed(1) || '—', lower: true },
  { key: 'kBBRatio', label: 'K/BB', fmt: v => v?.toFixed(2) || '—' },
  { key: 'hitsPer9', label: 'H/9', fmt: v => v?.toFixed(1) || '—', lower: true },
];
const BATTER_RADAR_DIMS = [
  { key: 'avg', label: 'AVG', max: 0.350 }, { key: 'obp', label: 'OBP', max: 0.430 },
  { key: 'slg', label: 'SLG', max: 0.600 }, { key: 'iso', label: 'ISO', max: 0.300 },
  { key: 'wOBA', label: 'wOBA', max: 0.420 }, { key: 'babip', label: 'BABIP', max: 0.370 },
];
const PITCHER_RADAR_DIMS = [
  { key: 'kPer9', label: 'K/9', max: 14 }, { key: 'kBBRatio', label: 'K/BB', max: 6 },
  { key: 'era_inv', label: 'ERA', max: 1, invert: true, src: 'era', invMax: 6 },
  { key: 'whip_inv', label: 'WHIP', max: 1, invert: true, src: 'whip', invMax: 2 },
  { key: 'fip_inv', label: 'FIP', max: 1, invert: true, src: 'fip', invMax: 6 },
  { key: 'hitsPer9_inv', label: 'H/9', max: 1, invert: true, src: 'hitsPer9', invMax: 12 },
];
const MAX_PLAYERS = 4;
const SLOT_COLORS = ['#1976D2', '#E91E63', '#4CAF50', '#FF9800'];
const SLOT_LABELS = ['A', 'B', 'C', 'D'];

const PlayerSearchBox = memo(({ label, value, onChange, inputValue, onInputChange, results, sLoading, onRemove, canRemove }) => (
  <Box sx={{ flex: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Autocomplete freeSolo options={results}
      getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${(opt.name || '').replace(/_/g, ' ')} (${opt.team || ''})`}
      loading={sLoading} inputValue={inputValue}
      onInputChange={(_, val, reason) => { if (reason !== 'blur') onInputChange(val, reason === 'reset'); }}
      onChange={(_, val) => { if (val && typeof val === 'object') onChange(val); }}
      renderOption={(props, opt) => (
        <li {...props} key={`${opt.team}-${opt.name}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={getTeamLogoUrl(opt.team)} sx={{ width: 22, height: 22 }} />
            <Typography variant="body2" fontWeight={600}>{(opt.name || '').replace(/_/g, ' ')}</Typography>
            <Chip label={opt.team} size="small" sx={{ height: 18, fontSize: '0.75rem' }} />
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} variant="outlined" size="small"
          InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }} />
      )}
      sx={{ flex: 1 }} />
    {canRemove && (
      <IconButton size="small" onClick={onRemove} sx={{ color: 'text.secondary' }}>
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
));
PlayerSearchBox.displayName = 'PlayerSearchBox';

const ComparePlayersTab = ({ year, isMobile, navigate }) => {
  const theme = useTheme();
  const [players, setPlayers] = useState(Array(MAX_PLAYERS).fill(null));
  const [searches, setSearches] = useState(Array(MAX_PLAYERS).fill(''));
  const [searchResults, setSearchResults] = useState(Array(MAX_PLAYERS).fill([]));
  const [searchLoading, setSearchLoading] = useState(Array(MAX_PLAYERS).fill(false));
  const [comparison, setComparison] = useState(null);
  const [profiles, setProfiles] = useState(Array(MAX_PLAYERS).fill(null));
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [slotCount, setSlotCount] = useState(2);
  const timersRef = useRef([]);

  const updateArr = (setter, idx, val) => setter(prev => { const n = [...prev]; n[idx] = val; return n; });

  const handleSearchInput = (idx, text, isReset = false) => {
    updateArr(setSearches, idx, text);
    if (isReset) return; // Don't re-search after option selection
    clearTimeout(timersRef.current[idx]);
    if (!text || text.length < 2) { updateArr(setSearchResults, idx, []); return; }
    timersRef.current[idx] = setTimeout(async () => {
      updateArr(setSearchLoading, idx, true);
      try {
        const res = await playersApi.searchUnified(text, { limit: 10 });
        const raw = res?.results || res?.players || [];
        const normalized = raw.map(r => ({
          ...r,
          name: (r.name || r.player || r.fullName || '').replace(/_/g, ' '),
          team: r.team || r.teamAbbr || r.currentTeam || '',
        }));
        updateArr(setSearchResults, idx, normalized);
      } catch { updateArr(setSearchResults, idx, []); }
      finally { updateArr(setSearchLoading, idx, false); }
    }, 300);
  };

  const handleSelectPlayer = (idx, val) => { if (val && typeof val === 'object') updateArr(setPlayers, idx, val); };
  const handleRemoveSlot = (idx) => {
    if (slotCount <= 2) return;
    const newPlayers = [...players]; newPlayers.splice(idx, 1); newPlayers.push(null);
    const newSearches = [...searches]; newSearches.splice(idx, 1); newSearches.push('');
    const newResults = [...searchResults]; newResults.splice(idx, 1); newResults.push([]);
    setPlayers(newPlayers); setSearches(newSearches); setSearchResults(newResults);
    setSlotCount(s => s - 1);
  };

  const selectedPlayers = players.filter(Boolean);
  const doCompare = useCallback(async () => {
    const selected = players.filter(Boolean);
    if (selected.length < 2) { setComparison(null); return; }
    setLoading(true);
    try {
      // Use the player-index 'keys' dict when available (handles accented names correctly)
      const getSlug = (p) => {
        if (p.keys) {
          const k = p.keys[`${p.team}-${year}`] || Object.values(p.keys).find(v => v.endsWith(`-${year}`));
          if (k) return k;
        }
        return `${p.team}-${(p.name || '').trim().replace(/\s+/g, '_')}-${year}`;
      };
      const slugs = selected.map(getSlug);
      const promises = [
        playersApi.comparePlayers(slugs, { year, categories: ['batting', 'pitching'] }),
        ...slugs.map(s => analyticsApi.getPlayerProfile(s, year)),
      ];
      const results = await Promise.allSettled(promises);
      setComparison(results[0].status === 'fulfilled' ? results[0].value : null);
      const newProfiles = Array(MAX_PLAYERS).fill(null);
      selected.forEach((sel, i) => {
        const origIdx = players.indexOf(sel);
        newProfiles[origIdx] = results[i + 1]?.status === 'fulfilled' ? results[i + 1].value : null;
      });
      setProfiles(newProfiles);
    } catch { setComparison(null); }
    finally { setLoading(false); }
  }, [players, year]);

  useEffect(() => { doCompare(); }, [doCompare]);

  const compPlayers = (comparison?.comparisons || comparison?.players || []).slice(0, selectedPlayers.length);
  const isPitcher = (p) => p?.stats?.pitching?.era != null && (p?.stats?.pitching?.inningsPitched || 0) > 0;
  const showPitching = compPlayers.length >= 2 && compPlayers.every(p => isPitcher(p));
  const statList = showPitching ? CP_PITCHING_STATS : CP_BATTING_STATS;
  const statType = showPitching ? 'pitching' : 'batting';
  const getVal = (player, stat) => player?.stats?.[statType]?.[stat.key];
  const getBestIdx = (stat, values) => {
    let bestIdx = null, bestVal = null;
    values.forEach((v, i) => {
      if (v == null) return;
      const n = parseFloat(v); if (isNaN(n)) return;
      if (bestVal === null) { bestIdx = i; bestVal = n; return; }
      if (stat.lower ? n < bestVal : n > bestVal) { bestIdx = i; bestVal = n; }
    });
    return bestIdx;
  };
  const slotColors = players.map((p, i) => p ? (themeUtils.getTeamColor(compPlayers[i]?.team || p.team) || SLOT_COLORS[i]) : SLOT_COLORS[i]);
  const buildRadarData = () => {
    if (compPlayers.length < 2) return [];
    const dims = showPitching ? PITCHER_RADAR_DIMS : BATTER_RADAR_DIMS;
    return dims.map(dim => {
      const item = { stat: dim.label };
      compPlayers.forEach((cp, i) => {
        if (dim.invert) {
          const raw = parseFloat(cp.stats?.[statType]?.[dim.src] || 0);
          item[`p${i}`] = Math.max(0, Math.min(100, ((dim.invMax - raw) / dim.invMax) * 100));
        } else {
          const raw = parseFloat(cp.stats?.[statType]?.[dim.key] || 0);
          item[`p${i}`] = Math.max(0, Math.min(100, (raw / dim.max) * 100));
        }
      });
      return item;
    });
  };

  return (
    <Box>
      {/* Search Row */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {Array.from({ length: slotCount }).map((_, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <CompareArrows sx={{ color: 'text.secondary', fontSize: 22, display: { xs: 'none', sm: 'block' } }} />}
              <PlayerSearchBox
                label={`Player ${SLOT_LABELS[idx]}`}
                value={players[idx]}
                onChange={(val) => handleSelectPlayer(idx, val)}
                inputValue={searches[idx]}
                onInputChange={(val, isReset) => handleSearchInput(idx, val, isReset)}
                results={searchResults[idx]}
                sLoading={searchLoading[idx]}
                onRemove={() => handleRemoveSlot(idx)}
                canRemove={slotCount > 2} />
            </React.Fragment>
          ))}
          {slotCount < MAX_PLAYERS && (
            <Tooltip title="Add player slot">
              <IconButton onClick={() => setSlotCount(s => s + 1)} sx={{ border: '1px dashed', borderColor: 'divider', width: 36, height: 36 }}>
                <Add sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Card>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>}

      {compPlayers.length >= 2 && !loading && (
        <Box>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {compPlayers.map((p, idx) => {
              const tc = slotColors[idx];
              const prof = profiles[idx];
              const winsCount = statList.reduce((acc, stat) => {
                const values = compPlayers.map(cp => getVal(cp, stat));
                return acc + (getBestIdx(stat, values) === idx ? 1 : 0);
              }, 0);
              const war = prof?.war; const cvr = prof?.cvr;
              const gridSize = compPlayers.length <= 2 ? 6 : compPlayers.length === 3 ? 4 : 3;
              return (
                <Grid item xs={6} sm={gridSize} key={idx}>
                  <Card elevation={0} onClick={() => navigate(playerProfileUrl((p.name || '').replace(/_/g, ' ')))}
                    sx={{ borderRadius: 2, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${alpha(tc, 0.3)}`, '&:hover': { borderColor: tc }, transition: 'border-color 0.15s' }}>
                    <Box sx={{ background: `linear-gradient(135deg, ${alpha(tc, 0.8)}, ${alpha(tc, 0.5)})`, py: 0.4, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Player {SLOT_LABELS[idx]}</Typography>
                    </Box>
                    <Box sx={{ p: 1.5, textAlign: 'center' }}>
                      <Avatar src={getTeamLogoUrl(p.team)} sx={{ width: 40, height: 40, mx: 'auto', mb: 0.5 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                      <Typography variant="body2" fontWeight={800} noWrap sx={{ fontSize: '0.9rem' }}>{(p.name || '').replace(/_/g, ' ')}</Typography>
                      <Chip label={p.team} size="small" sx={{ mt: 0.5, fontWeight: 600, bgcolor: alpha(tc, 0.1), color: tc, height: 20, fontSize: '0.72rem' }} />
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                        <Chip icon={<EmojiEvents sx={{ fontSize: '12px !important', color: '#FFD700 !important' }} />} label={`${winsCount}`} size="small" sx={{ height: 20, fontSize: '0.72rem', fontWeight: 700 }} />
                        {war != null && <Chip size="small" label={`WAR ${parseFloat(war).toFixed(1)}`} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 800, fontFamily: 'monospace', bgcolor: parseFloat(war) >= 4 ? alpha('#FFD700', 0.15) : alpha(theme.palette.divider, 0.1), color: parseFloat(war) >= 4 ? '#B8860B' : 'text.secondary' }} />}
                        {cvr?.cvr != null && <Chip size="small" label={`CVR ${parseFloat(cvr.cvr).toFixed(0)}`} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 800, fontFamily: 'monospace', bgcolor: alpha(CVR_TIER_COLORS[cvr.tier] || '#666', 0.15), color: CVR_TIER_COLORS[cvr.tier] || 'text.secondary' }} />}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" sx={{ mb: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', minHeight: 40 } }}>
            <Tab icon={<CompareArrows sx={{ fontSize: 16 }} />} iconPosition="start" label="Stats" />
            <Tab icon={<Speed sx={{ fontSize: 16 }} />} iconPosition="start" label="Radar" />
            <Tab icon={<Assessment sx={{ fontSize: 16 }} />} iconPosition="start" label="Analytics" />
          </Tabs>

          <AnimatePresence mode="wait">
            {activeTab === 0 && (
              <motion.div key="stats" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ py: 0.75, px: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CompareArrows sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={700}>{showPitching ? 'Pitching' : 'Batting'} Comparison</Typography>
                  </Box>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 60 }}>Stat</TableCell>
                          {compPlayers.map((cp, i) => <TableCell key={i} align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', color: slotColors[i], minWidth: 80 }}>{(cp.name || '').replace(/_/g, ' ').split(' ').pop()}</TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statList.map(stat => {
                          const values = compPlayers.map(cp => getVal(cp, stat));
                          const bestI = getBestIdx(stat, values);
                          return (
                            <TableRow key={stat.key}>
                              <TableCell align="center" sx={{ py: 0.6, fontWeight: 700, fontSize: '0.75rem', bgcolor: alpha(theme.palette.divider, 0.06) }}>{stat.label}</TableCell>
                              {values.map((v, i) => (
                                <TableCell key={i} align="center" sx={{ py: 0.6, fontFamily: 'monospace', fontWeight: bestI === i ? 800 : 500, fontSize: '0.82rem', color: bestI === i ? 'success.main' : 'text.primary', bgcolor: bestI === i ? alpha('#2e7d32', 0.04) : 'transparent' }}>
                                  {stat.fmt(v)}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Card>
                {/* Visual bars */}
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ py: 0.5, px: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`, color: 'white', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <SportsBaseball sx={{ fontSize: 14 }} /><Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>Visual Comparison</Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {statList.slice(0, 8).map(stat => {
                      const values = compPlayers.map(cp => getVal(cp, stat) || 0);
                      const max = Math.max(...values, 0.001);
                      const bestI = getBestIdx(stat, values);
                      return (
                        <Box key={stat.key} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.25 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.72rem' }}>{stat.label}</Typography>
                          </Box>
                          {values.map((v, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.3 }}>
                              <Typography variant="caption" sx={{ width: 20, fontSize: '0.68rem', fontWeight: 700, color: slotColors[i], textAlign: 'center' }}>{SLOT_LABELS[i]}</Typography>
                              <Box sx={{ flex: 1, height: 8, bgcolor: alpha(theme.palette.divider, 0.08), borderRadius: 1, overflow: 'hidden' }}>
                                <Box sx={{ width: `${(v / max) * 100}%`, height: '100%', borderRadius: 1, bgcolor: alpha(slotColors[i], 0.75), transition: 'width 0.5s ease' }} />
                              </Box>
                              <Typography variant="caption" fontWeight={bestI === i ? 800 : 600} fontFamily="monospace" sx={{ width: 50, textAlign: 'right', fontSize: '0.72rem', color: bestI === i ? 'success.main' : 'text.primary' }}>{stat.fmt(v)}</Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              </motion.div>
            )}
            {activeTab === 1 && (
              <motion.div key="radar" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ py: 0.5, px: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: 'white', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Speed sx={{ fontSize: 14 }} /><Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>{showPitching ? 'Pitching' : 'Batting'} Radar Overlay</Typography>
                  </Box>
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <ResponsiveContainer width="100%" height={340}>
                      <RadarChart data={buildRadarData()} outerRadius={120}>
                        <PolarGrid stroke={alpha(theme.palette.divider, 0.3)} />
                        <PolarAngleAxis dataKey="stat" tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontWeight: 700 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                        {compPlayers.map((cp, i) => (
                          <Radar key={i} name={(cp.name || '').replace(/_/g, ' ')} dataKey={`p${i}`} stroke={slotColors[i]} fill={slotColors[i]} fillOpacity={0.15} strokeWidth={2} />
                        ))}
                        <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', textAlign: 'center', display: 'block' }}>
                      {showPitching ? 'Lower-is-better stats (ERA, WHIP, FIP, H/9) are inverted so larger area = better.' : 'Each axis scaled to elite thresholds. Larger area = stronger overall profile.'}
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            )}
            {activeTab === 2 && (
              <motion.div key="analytics" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ py: 0.5, px: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: 'white', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Assessment sx={{ fontSize: 14 }} /><Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>Advanced Analytics</Typography>
                  </Box>
                  {profiles.every(p => !p) ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Advanced analytics not available for these players.</Typography></Box>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 80 }}>Metric</TableCell>
                            {compPlayers.map((cp, i) => <TableCell key={i} align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', color: slotColors[i], minWidth: 70 }}>{(cp.name || '').replace(/_/g, ' ').split(' ').pop()}</TableCell>)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow><TableCell colSpan={compPlayers.length + 1} sx={{ py: 0.5, fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', bgcolor: alpha(theme.palette.divider, 0.04) }}>Value Metrics</TableCell></TableRow>
                          {[
                            { label: 'WAR', key: 'war', fmt: v => parseFloat(v).toFixed(1) },
                            { label: 'CVR', key: 'cvr.cvr', fmt: v => parseFloat(v).toFixed(0) },
                            { label: 'Trade Val', key: 'tradeValue.totalValue', fmt: v => parseFloat(v).toFixed(0), alt: 'tradeValue.value' },
                          ].map(metric => {
                            const vals = compPlayers.map((_, i) => {
                              const prof = profiles[i]; if (!prof) return null;
                              const keys = metric.key.split('.'); let val = prof;
                              for (const k of keys) { val = val?.[k]; }
                              if (val == null && metric.alt) { val = prof; for (const k of metric.alt.split('.')) { val = val?.[k]; } }
                              return val;
                            });
                            const numVals = vals.map(v => v != null ? parseFloat(v) : null);
                            const bestI = numVals.reduce((best, v, i) => v != null && (best === null || v > numVals[best]) ? i : best, null);
                            return (
                              <TableRow key={metric.label}>
                                <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: '0.75rem' }}>{metric.label}</TableCell>
                                {vals.map((v, i) => <TableCell key={i} align="center" sx={{ py: 0.5, fontFamily: 'monospace', fontWeight: bestI === i ? 800 : 500, fontSize: '0.82rem', color: bestI === i ? 'success.main' : 'text.primary' }}>{v != null ? metric.fmt(v) : '—'}</TableCell>)}
                              </TableRow>
                            );
                          })}
                          {profiles.some(p => p?.cvr?.tier) && (
                            <TableRow>
                              <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem' }}>CVR Tier</TableCell>
                              {compPlayers.map((_, i) => (
                                <TableCell key={i} align="center" sx={{ py: 0.75 }}>
                                  {profiles[i]?.cvr?.tier ? (
                                    <Chip size="small" label={profiles[i].cvr.tier} sx={{ fontWeight: 800, fontSize: '0.72rem', height: 22, bgcolor: alpha(CVR_TIER_COLORS[profiles[i].cvr.tier] || '#666', 0.15), color: CVR_TIER_COLORS[profiles[i].cvr.tier] || 'text.secondary' }} />
                                  ) : '—'}
                                </TableCell>
                              ))}
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      )}

      {selectedPlayers.length < 2 && !loading && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center' }}>
          <CompareArrows sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Select Players to Compare</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 440, mx: 'auto' }}>
            Search for 2–4 players to see side-by-side stats, radar overlays, and advanced analytics (WAR, CVR, trade value).
          </Typography>
        </Card>
      )}
    </Box>
  );
};

// ============================================================================
// COMPARE TEAMS TAB
// ============================================================================

const TEAM_BATTING_RADAR = [
  { key: 'avg', label: 'AVG', max: .350 }, { key: 'obp', label: 'OBP', max: .400 },
  { key: 'slg', label: 'SLG', max: .500 }, { key: 'ops', label: 'OPS', max: .900 },
  { key: 'homeRuns', label: 'HR', max: 250 }, { key: 'stolenBases', label: 'SB', max: 150 },
];
const TEAM_PITCHING_RADAR = [
  { key: 'era', label: 'ERA', max: 6.0, invert: true }, { key: 'whip', label: 'WHIP', max: 1.6, invert: true },
  { key: 'strikeouts', label: 'K', max: 1600 }, { key: 'saves', label: 'SV', max: 60 },
  { key: 'qualityStarts', label: 'QS', max: 100 }, { key: 'shutouts', label: 'SHO', max: 15 },
];
const normalizeRadarVal = (val, def) => {
  if (val == null) return 0;
  const norm = def.invert ? (1 - val / def.max) * 100 : (val / def.max) * 100;
  return Math.max(0, Math.min(100, norm));
};

const CompareTeamsTab = ({ year, isMobile, navigate }) => {
  const theme = useTheme();
  const [team1, setTeam1] = useState(null);
  const [team2, setTeam2] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchComparison = useCallback(async () => {
    if (!team1 || !team2) return;
    try {
      setLoading(true); setError(null);
      const data = await teamsApi.compareTeams([team1.abbr, team2.abbr], { year, categories: ['batting', 'pitching', 'fielding'] });
      setComparison(data);
    } catch (err) { setError(err.message || 'Failed to compare teams'); setComparison(null); }
    finally { setLoading(false); }
  }, [team1, team2, year]);

  useEffect(() => { if (team1 && team2) fetchComparison(); }, [team1, team2, year, fetchComparison]);

  const t1 = comparison?.comparisons?.[0];
  const t2 = comparison?.comparisons?.[1];

  const battingRadar = (t1 && t2) ? TEAM_BATTING_RADAR.map(def => ({
    stat: def.label, [t1.id]: normalizeRadarVal(t1.stats?.batting?.[def.key], def), [t2.id]: normalizeRadarVal(t2.stats?.batting?.[def.key], def),
  })) : [];
  const pitchingRadar = (t1 && t2) ? TEAM_PITCHING_RADAR.map(def => ({
    stat: def.label, [t1.id]: normalizeRadarVal(t1.stats?.pitching?.[def.key], def), [t2.id]: normalizeRadarVal(t2.stats?.pitching?.[def.key], def),
  })) : [];

  const renderTeamSelector = (value, onChange, label) => (
    <Autocomplete value={value} onChange={(e, v) => onChange(v)} options={ALL_TEAMS}
      getOptionLabel={(o) => `${o.abbr} - ${o.name}`}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={getTeamLogoUrl(option.abbr)} sx={{ width: 24, height: 24 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{option.abbr[0]}</Avatar>
          <Typography variant="body2" fontWeight={600}>{option.abbr}</Typography>
          <Typography variant="body2" color="text.secondary">{option.name}</Typography>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} size="small" />}
      sx={{ minWidth: isMobile ? '100%' : 260 }} />
  );

  const formatStat = (val) => {
    if (val == null) return '—';
    if (typeof val === 'number') return val < 1 && val > 0 ? val.toFixed(3) : val % 1 === 0 ? val : val.toFixed(2);
    return val;
  };
  const renderStatRow = (label, key, data1, data2, lowerBetter = false) => {
    const v1 = data1?.[key], v2 = data2?.[key];
    const better1 = lowerBetter ? v1 < v2 : v1 > v2;
    const better2 = lowerBetter ? v2 < v1 : v2 > v1;
    return (
      <TableRow key={key}>
        <TableCell align="right" sx={{ fontWeight: better1 && v1 != null && v2 != null ? 700 : 400, color: better1 ? 'success.main' : 'text.primary' }}>{formatStat(v1)}</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{label}</TableCell>
        <TableCell align="left" sx={{ fontWeight: better2 && v1 != null && v2 != null ? 700 : 400, color: better2 ? 'success.main' : 'text.primary' }}>{formatStat(v2)}</TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <Card sx={{ mb: 2, p: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', justifyContent: 'center' }}>
          {renderTeamSelector(team1, setTeam1, 'Team 1')}
          <Chip icon={<CompareArrows />} label="VS" sx={{ fontWeight: 700 }} />
          {renderTeamSelector(team2, setTeam2, 'Team 2')}
        </Box>
      </Card>

      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && comparison && t1 && t2 && (
        <>
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar src={getTeamLogoUrl(t1.id)} sx={{ width: 48, height: 48, mx: 'auto', mb: 1 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{t1.id?.[0]}</Avatar>
                  <Typography variant="h6" fontWeight={700}>{t1.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{t1.record?.wins}-{t1.record?.losses}</Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} color="text.disabled">VS</Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar src={getTeamLogoUrl(t2.id)} sx={{ width: 48, height: 48, mx: 'auto', mb: 1 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{t2.id?.[0]}</Avatar>
                  <Typography variant="h6" fontWeight={700}>{t2.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{t2.record?.wins}-{t2.record?.losses}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }} variant={isMobile ? 'scrollable' : 'standard'} centered={!isMobile}>
            <Tab label="Batting" /><Tab label="Pitching" /><Tab label="Summary" />
          </Tabs>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Batting Radar</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={battingRadar}>
                        <PolarGrid /><PolarAngleAxis dataKey="stat" tick={{ fontSize: 11, fontWeight: 600 }} /><PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar name={t1.id} dataKey={t1.id} stroke={theme.palette.primary.main} fill={theme.palette.primary.main} fillOpacity={0.25} />
                        <Radar name={t2.id} dataKey={t2.id} stroke={theme.palette.secondary.main} fill={theme.palette.secondary.main} fillOpacity={0.25} />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Batting Stats</Typography>
                    <Table size="small">
                      <TableHead><TableRow><TableCell align="right" sx={{ fontWeight: 700 }}>{t1.id}</TableCell><TableCell align="center" sx={{ fontWeight: 700 }}>Stat</TableCell><TableCell align="left" sx={{ fontWeight: 700 }}>{t2.id}</TableCell></TableRow></TableHead>
                      <TableBody>
                        {renderStatRow('AVG', 'avg', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('OBP', 'obp', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('SLG', 'slg', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('OPS', 'ops', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('HR', 'homeRuns', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('R', 'runs', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('RBI', 'rbi', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('SB', 'stolenBases', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('BB', 'baseOnBalls', t1.stats?.batting, t2.stats?.batting)}
                        {renderStatRow('K', 'strikeOuts', t1.stats?.batting, t2.stats?.batting, true)}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Pitching Radar</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={pitchingRadar}>
                        <PolarGrid /><PolarAngleAxis dataKey="stat" tick={{ fontSize: 11, fontWeight: 600 }} /><PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar name={t1.id} dataKey={t1.id} stroke={theme.palette.primary.main} fill={theme.palette.primary.main} fillOpacity={0.25} />
                        <Radar name={t2.id} dataKey={t2.id} stroke={theme.palette.secondary.main} fill={theme.palette.secondary.main} fillOpacity={0.25} />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Pitching Stats</Typography>
                    <Table size="small">
                      <TableHead><TableRow><TableCell align="right" sx={{ fontWeight: 700 }}>{t1.id}</TableCell><TableCell align="center" sx={{ fontWeight: 700 }}>Stat</TableCell><TableCell align="left" sx={{ fontWeight: 700 }}>{t2.id}</TableCell></TableRow></TableHead>
                      <TableBody>
                        {renderStatRow('ERA', 'era', t1.stats?.pitching, t2.stats?.pitching, true)}
                        {renderStatRow('WHIP', 'whip', t1.stats?.pitching, t2.stats?.pitching, true)}
                        {renderStatRow('K', 'strikeOuts', t1.stats?.pitching, t2.stats?.pitching)}
                        {renderStatRow('W', 'wins', t1.stats?.pitching, t2.stats?.pitching)}
                        {renderStatRow('SV', 'saves', t1.stats?.pitching, t2.stats?.pitching)}
                        {renderStatRow('IP', 'inningsPitched', t1.stats?.pitching, t2.stats?.pitching)}
                        {renderStatRow('BB', 'baseOnBalls', t1.stats?.pitching, t2.stats?.pitching, true)}
                        {renderStatRow('QS', 'qualityStarts', t1.stats?.pitching, t2.stats?.pitching)}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {activeTab === 2 && (() => {
            const b1 = t1.stats?.batting || {}, b2 = t2.stats?.batting || {};
            const p1 = t1.stats?.pitching || {}, p2 = t2.stats?.pitching || {};
            const w1 = t1.record?.wins || 0, w2 = t2.record?.wins || 0;
            const overall = w1 > w2 ? t1.id : w2 > w1 ? t2.id : 'Even';
            const battingEdges = [];
            if ((b1.ops || 0) > (b2.ops || 0)) battingEdges.push(`${t1.id} has higher OPS (${(b1.ops || 0).toFixed(3)} vs ${(b2.ops || 0).toFixed(3)})`);
            else if ((b2.ops || 0) > (b1.ops || 0)) battingEdges.push(`${t2.id} has higher OPS`);
            if ((b1.homeRuns || 0) > (b2.homeRuns || 0)) battingEdges.push(`${t1.id} leads in HR (${b1.homeRuns} vs ${b2.homeRuns})`);
            else if ((b2.homeRuns || 0) > (b1.homeRuns || 0)) battingEdges.push(`${t2.id} leads in HR`);
            const pitchEdges = [];
            if ((p1.era || 99) < (p2.era || 99)) pitchEdges.push(`${t1.id} has lower ERA`);
            else if ((p2.era || 99) < (p1.era || 99)) pitchEdges.push(`${t2.id} has lower ERA`);
            return (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom><EmojiEvents sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />Comparison Summary</Typography>
                  <Chip label={`Overall Edge: ${overall}`} color="primary" sx={{ fontWeight: 700, mb: 2, fontSize: '0.9rem' }} />
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Record:</strong> {t1.id} ({w1}-{t1.record?.losses || 0}) vs {t2.id} ({w2}-{t2.record?.losses || 0})</Typography>
                  {battingEdges.length > 0 && <Box sx={{ mb: 1 }}><Typography variant="subtitle2" fontWeight={700}>Batting</Typography>{battingEdges.map((e, i) => <Typography key={i} variant="body2" color="text.secondary">{e}</Typography>)}</Box>}
                  {pitchEdges.length > 0 && <Box sx={{ mb: 1 }}><Typography variant="subtitle2" fontWeight={700}>Pitching</Typography>{pitchEdges.map((e, i) => <Typography key={i} variant="body2" color="text.secondary">{e}</Typography>)}</Box>}
                </CardContent>
              </Card>
            );
          })()}
        </>
      )}

      {!team1 && !team2 && (
        <Card sx={{ borderRadius: 3, mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Groups sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>Select two teams to compare</Typography>
            <Typography variant="body2" color="text.disabled">Choose teams above to see head-to-head stat comparisons with radar charts</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export { PitchExplorer, AtBatExplorerTab };
export default Explorer;
